import { getFirestore, initializeFirestore } from 'firebase/firestore';
import { app } from '../../config/firebase';
import type { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore';

// initializeFirestore must run before any getFirestore() call on the same app.
// ignoreUndefinedProperties: prevents `Unsupported field value: undefined` errors
// when writing nested objects (e.g. actionData.principalCount from G11 when
// principals is empty). Mirrors the same setting on the functions-side admin
// SDK (functions/shield-g11-handler.js _getAdminDb).
// 2026-07-11: audit.service.ts was throwing FirebaseError on every G11 run
// with 0 principals because actionData.principalCount was undefined.
try {
  initializeFirestore(app, { ignoreUndefinedProperties: true });
} catch {
  // Already initialized — settings can't be changed after init. Non-fatal.
}

export const db = getFirestore(app);

export function tsToDate(ts: unknown): Date | null {
  if (!ts) return null;
  if (
    typeof ts === 'object' &&
    ts !== null &&
    'toDate' in ts &&
    typeof (ts as { toDate: () => Date }).toDate === 'function'
  ) {
    return (ts as { toDate: () => Date }).toDate();
  }
  if (ts instanceof Date) return ts;
  if (typeof ts === 'string') {
    const parsed = Date.parse(ts);
    if (!isNaN(parsed)) return new Date(parsed);
  }
  if (typeof ts === 'number') return new Date(ts);
  return null;
}

export function dateToTs(date: Date | null) {
  if (!date) return null;
  // Firestore will auto-convert Date to Timestamp when writing
  return date;
}

export function createConverter<T>(fromRaw: (data: DocumentData, id: string) => T) {
  return {
    toFirestore: (data: T) => data as unknown as DocumentData,
    fromFirestore: (snap: QueryDocumentSnapshot) => fromRaw(snap.data(), snap.id),
  };
}
